/**
 * A wrapper around FileViewer that implements the methods to view Azure Blob storage files
 */

import { toast } from 'react-hot-toast'
import React, { useCallback, useEffect } from 'react'
import axios from 'axios'
import { trpc } from '../../../core/trpc'
import { downloadFileFromLink } from '../../../models/file'
import { FileViewerDialog, FileViewerMenuAction } from './FileViewerDialog'
import { FileViewerContents } from './FileViewerContents'
import { FileOutput } from '../../../types/procedureOutputs'
import { SystemFileSidebar } from './SystemFileSidebar'

export interface BlobStorageFileViewerProps {
  open: boolean
  onClose: () => void
  onFileUpdate: () => void
  file: FileOutput

  pagination: {
    hasPrevFile: boolean
    onPrevFile: () => void
    hasNextFile: boolean
    onNextFile: () => void
  }
}

function BlobStorageFileViewer(props: BlobStorageFileViewerProps) {
  const { file, open, onClose, pagination, onFileUpdate } = props

  const [loadingProgress, setLoadingProgress] = React.useState(0)
  const [fileContents, setFileContents] = React.useState<ArrayBuffer | null>(null)

  const deleteFileMutation = trpc.file.deleteFile.useMutation({
    onSuccess: () => {
      toast.success('Successfully deleted file')
    },
    onError: () => toast.error('File deletion failed')
  })

  const updateFileMutation = trpc.file.updateFile.useMutation({
    onSuccess: () => {
      toast.success('Successfully updated file')
    },
    onError: () => toast.error('File update failed')
  })

  const getViewFileLink = trpc.file.viewFileLink.useMutation()

  const handleSelectAction = useCallback(
    async (action: FileViewerMenuAction) => {
      switch (action) {
        case FileViewerMenuAction.Download:
          try {
            const url = (await getViewFileLink.mutateAsync({ id: file!.id })) as unknown as string
            await downloadFileFromLink(file!, url)
          } catch (e) {
            toast.error(`Failed to download file. ${e}`)
          }

          break
        case FileViewerMenuAction.MarkAsContract:
          await updateFileMutation.mutateAsync({ id: file!.id, is_contract: !file!.is_contract })
          break

        case FileViewerMenuAction.Delete:
          await deleteFileMutation.mutateAsync({ id: file!.id })
          break
      }
    },
    [deleteFileMutation, getViewFileLink, updateFileMutation]
  )

  const handleLoadFile = useCallback(async () => {
    const url = await getViewFileLink.mutateAsync({ id: file.id })

    const response = await axios.get<ArrayBuffer>(url, {
      responseType: 'arraybuffer',
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          setLoadingProgress(progress)
        }
      }
    })

    setFileContents(response.data)
    // type: response.headers['content-type'] || ''
  }, [file.id])

  useEffect(() => {
    if (open) {
      handleLoadFile()
    }
  }, [open, handleLoadFile])

  return (
    <FileViewerDialog open={open} onClose={onClose}>
      <FileViewerContents
        fileMeta={{
          name: file.name,
          type: file.type
        }}
        fileData={fileContents}
        loadingProgress={loadingProgress}
      />
      <SystemFileSidebar
        file={file}
        actions={[FileViewerMenuAction.Download, FileViewerMenuAction.MarkAsContract, FileViewerMenuAction.Delete]}
        onSelectAction={handleSelectAction}
        onClose={onClose}
        onFileUpdate={onFileUpdate}
        {...pagination}
      />
    </FileViewerDialog>
  )
}

export default React.memo(BlobStorageFileViewer)
